import { Box, FormControl, FormHelperText, Grid, Paper } from '@mui/material';
import { Button } from '@biq/apex-ui';
import ReactCodeMirror from '@uiw/react-codemirror';
import { FormLabel } from 'components';
import {
    sharedStyles,
    useMinWidthMediaQueries,
    BREAKPOINT_KEYS,
    CodeMirrorStyles,
} from 'lib';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    AdvancedFilterSchema,
    AdvancedFormInputs,
    advancedFilterStyles,
    styles,
    FilterMode,
} from './life-graph-filters-helpers';
import { LifeGraphExplorerFilter } from '../lifegraph-explorer/lifegraph-explorer.types';
import { useAlert, useAuth } from 'core/contexts';
import { useValidateGraphChain } from 'hooks';
import { CustomIconButton } from 'components/custom-icon-button/CustomIconButton';
import { HistoryComponent } from './HistoryComponent/HistoryComponent';

interface LifeGraphAdvancedFilterProps {
    setFilter: (
        newFilter: LifeGraphExplorerFilter | string | undefined
    ) => void;
    handleFilterModeChange: (mode: FilterMode) => void;
    defaultValues: AdvancedFormInputs;
}

export const LifeGraphAdvancedFilter = ({
    setFilter,
    handleFilterModeChange,
    defaultValues,
}: LifeGraphAdvancedFilterProps): React.JSX.Element => {
    const {
        handleSubmit,
        watch,
        register,
        setValue,
        formState: { errors },
    } = useForm<AdvancedFormInputs>({
        mode: 'all',
        resolver: yupResolver(AdvancedFilterSchema),
        defaultValues: defaultValues,
    });
    const isMediaBreakpoint: boolean = useMinWidthMediaQueries(
        BREAKPOINT_KEYS.MD
    );
    const [showHistory, setShowHistory] = useState<boolean>(false);
    const query = watch('query');
    const { showAlert, hideAlert } = useAlert();
    const { mutate: validateGraphChain } = useValidateGraphChain();
    const { getToken } = useAuth();

    const onSubmit = (formInputs: AdvancedFormInputs): void => {
        setFilter(formInputs.query);
    };

    const handleOnChange = (value: string): void => {
        setValue('query', value);
    };
    const handleCopyClick = (): void => {
        navigator.clipboard.writeText(query).then(() => {
            if (query) {
                showAlert({
                    severity: 'success',
                    variant: 'filled',
                    closable: true,
                    onClose: hideAlert,
                    children: 'TQL Query has been copied to clipboard',
                });
            }
        });
    };

    const handleDownloadTQL = (): void => {
        if (query) {
            validateGraphChain(
                { query, token: getToken() },
                {
                    onSuccess: (): void => {
                        const blob = new Blob([query], { type: 'text/plain' });
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(blob);
                        link.download = 'query.tql';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    },
                }
            );
        }
    };

    const handleHistoryClick = (): void => {
        setShowHistory(true);
    };

    const handleRowClick = (history: string): void => {
        setValue('query', history);
        onSubmit({
            query: history,
        });
    };

    const handleBackButtonClick = (): void => {
        setShowHistory(false);
    };

    return (
        <Paper sx={advancedFilterStyles.paper}>
            {!showHistory ? (
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={advancedFilterStyles.containerStyles}
                >
                    <Grid
                        direction='column'
                        container
                        sx={advancedFilterStyles.containerStyles}
                    >
                        <Grid
                            container
                            justifyContent={'space-between'}
                            alignItems='center'
                        >
                            <Box sx={advancedFilterStyles.labelBox}>
                                <FormLabel text='TQL' />
                            </Box>
                            <Box>
                                <CustomIconButton
                                    title='Copy'
                                    icon='mdi:content-copy'
                                    onClick={handleCopyClick}
                                />
                                <CustomIconButton
                                    title='Download'
                                    icon='mdi:download'
                                    onClick={handleDownloadTQL}
                                />
                                <CustomIconButton
                                    title='History'
                                    icon='mdi:history'
                                    onClick={handleHistoryClick}
                                />
                            </Box>
                        </Grid>
                        <Grid sx={advancedFilterStyles.codeMirrorWidth}>
                            <FormControl
                                fullWidth
                                sx={advancedFilterStyles.containerStyles}
                            >
                                <ReactCodeMirror
                                    {...register('query')}
                                    value={query}
                                    height={
                                        advancedFilterStyles.codeMirrorHeight
                                    }
                                    style={advancedFilterStyles.codeMirror}
                                    minHeight={
                                        advancedFilterStyles.codeMirrorMinHeight
                                    }
                                    onChange={handleOnChange}
                                    basicSetup={false}
                                    extensions={[CodeMirrorStyles]}
                                />
                            </FormControl>
                            {errors?.query && (
                                <FormHelperText
                                    sx={{
                                        ...sharedStyles.error,
                                    }}
                                >
                                    {errors?.query?.message}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid
                            container
                            justifyContent={'flex-end'}
                            sx={{
                                ...(isMediaBreakpoint
                                    ? styles.expandedButtonContainer
                                    : styles.minimizedButtonContainer),
                            }}
                        >
                            <Button
                                variant='contained'
                                type='submit'
                                size='small'
                                sx={advancedFilterStyles.applyBtn}
                            >
                                Apply
                            </Button>
                            <Button
                                sx={advancedFilterStyles.simpleBtn}
                                variant='outlined'
                                size='small'
                                onClick={(): void => {
                                    handleFilterModeChange('Basic');
                                }}
                            >
                                Simple Filters
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            ) : (
                <HistoryComponent
                    handleBackButtonClick={handleBackButtonClick}
                    handleApplyButtonClick={handleRowClick}
                />
            )}
        </Paper>
    );
};
